import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCDs } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { capitalizeWords, formatDate } from '../utils/cdUtils';

const ViewCDsPage = () => {
  const [cds, setCDs] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const navigate = useNavigate();
  const { isAuthenticated, logout } = useAuth();

  // Consistent style for all containers with white backing and gray gradient
  const containerWrapperStyle = {
    background: 'white',
    borderRadius: '0.75rem',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.3), 0 10px 10px -5px rgba(0, 0, 0, 0.2)',
    overflow: 'hidden',
    margin: '0 auto 1rem auto',
    padding: '0'
  };

  const containerStyle = {
    background: 'linear-gradient(to right, rgba(20, 32, 57, 0.9), rgba(30, 41, 59, 0.8))',
    borderRadius: '0.75rem',
    overflow: 'hidden',
    height: '100%',
    width: '100%'
  };

  // CD card container style with increased margin bottom
  const cdCardStyle = {
    ...containerWrapperStyle,
    marginBottom: '1.5rem' // Increased spacing between cards
  };

  const fetchCDs = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await getCDs();
      setCDs(result);
      const uniqueCompanies = [...new Set(result.map(cd => cd.company_name))];
      setCompanies(uniqueCompanies);
    } catch (error) {
      if (error.response && error.response.status === 401) {
        logout();
        navigate('/login');
      } else {
        setError('Failed to fetch research reports. Please try again later.');
        console.error('Error fetching CDs:', error);
      }
    } finally {
      setIsLoading(false);
    }
  }, [logout, navigate]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    } else {
      fetchCDs();
    }
  }, [isAuthenticated, navigate, fetchCDs]);

  const filterQueries = () => {
    let filtered = cds;
    
    if (selectedCompany) {
      filtered = filtered.filter(cd => cd.company_name === selectedCompany);
    }
    
    if (searchTerm) {
      filtered = filtered.filter(cd => 
        cd.company_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        cd.company_website.toLowerCase().includes(searchTerm.toLowerCase()) ||
        cd.stakeholders.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    return filtered.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  };

  const viewCDDetail = (cdId) => {
    navigate(`/view-cd/${cdId}`);
  };

  const handleCreateNew = () => {
    navigate('/create-cd');
  };

  // Pagination calculation
  const filteredCDs = filterQueries();
  const totalPages = Math.ceil(filteredCDs.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentCDs = filteredCDs.slice(indexOfFirstItem, indexOfLastItem);

  // Pagination controls
  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const nextPage = () => setCurrentPage(prev => Math.min(prev + 1, totalPages));
  const prevPage = () => setCurrentPage(prev => Math.max(prev - 1, 1));

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-royal-900 to-royal-800 py-12 px-4 sm:px-6 lg:px-8 font-poppins text-white">
        <div className="max-w-7xl mx-auto flex flex-col items-center justify-center min-h-[60vh]">
          <div className="relative w-24 h-24">
            <div className="absolute top-0 left-0 w-full h-full border-8 border-white/20 rounded-full"></div>
            <div className="absolute top-0 left-0 w-full h-full border-8 border-transparent border-t-orange-500 rounded-full animate-spin"></div>
          </div>
          <p className="mt-6 text-xl font-medium text-white">Loading your research...</p>
          <p className="mt-2 text-white/70">Fetching all your past company insights</p>
        </div>
      </div>
    );
  }

  if (error && cds.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-royal-900 to-royal-800 py-12 px-4 sm:px-6 lg:px-8 font-poppins text-white">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-500/20 border-l-4 border-red-500 p-6 rounded-lg mb-8">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-red-300">Error</h3>
                <p className="mt-1 text-white">{error}</p>
              </div>
            </div>
          </div>
          <button
            onClick={fetchCDs}
            className="px-6 py-3 bg-gradient-to-r from-orange-500 to-amber-600 text-white font-semibold rounded-lg shadow-md hover:from-orange-600 hover:to-amber-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-opacity-50 transition-all duration-200"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-royal-900 to-royal-800 py-12 px-4 sm:px-6 lg:px-8 font-poppins text-white">
      <div className="max-w-5xl mx-auto">
        {/* Header Section */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold mb-4 font-playfair">
            <span className="bg-gradient-to-r from-orange-400 to-orange-500 bg-clip-text text-transparent">
              View Contextual Dip Sticks
            </span>
          </h1>
          <div className="w-24 h-1 bg-gradient-to-r from-orange-400 to-orange-500 mx-auto mb-4"></div>
          <p className="text-royal-100 max-w-2xl mx-auto">
            Browse through your previously generated company research reports and insights.
          </p>
        </div>

        {/* Search and Filter Section */}
        <div style={containerWrapperStyle} className="mb-6"> {/* Increased bottom margin here */}
          <div style={containerStyle}>
            <div className="p-4">
              <div className="grid gap-4 md:grid-cols-3">
                {/* Search Input */}
                <div>
                  <label className="block text-white text-sm font-medium mb-1">
                    Search Research
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Search companies, websites, or stakeholders..."
                      className="w-full px-3 py-2 pl-9 rounded-lg bg-white/10 border border-white/20 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200 text-white placeholder-white/50 text-sm"
                    />
                    <svg className="absolute left-3 top-2.5 h-4 w-4 text-white/50" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                </div>

                {/* Company Filter */}
                <div>
                  <label className="block text-white text-sm font-medium mb-1">
                    Filter by Company
                  </label>
                  <select
                    value={selectedCompany}
                    onChange={(e) => setSelectedCompany(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg bg-white/10 border border-white/20 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200 text-white text-sm"
                  >
                    <option value="" className="bg-royal-800">All Companies</option>
                    {companies.map(company => (
                      <option key={company} value={company} className="bg-royal-800">
                        {capitalizeWords(company)}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Results Count */}
                <div>
                  <label className="block text-white text-sm font-medium mb-1">
                    Total Results
                  </label>
                  <div className="px-3 py-2 rounded-lg bg-white/10 border border-white/20 h-10 flex items-center">
                    <span className="text-orange-400 font-bold">{filteredCDs.length}</span>
                    <span className="text-white/70 text-sm ml-2">research reports</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Research Cards - Stacked Vertically */}
        {filteredCDs.length === 0 ? (
          <div style={containerWrapperStyle}>
            <div style={containerStyle}>
              <div className="text-center py-12">
                <div className="mx-auto w-20 h-20 bg-white/10 rounded-full flex items-center justify-center mb-4">
                  <svg className="w-10 h-10 text-white/50" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h3 className="text-xl font-medium text-white mb-2">No research found</h3>
                <p className="text-white/70 mb-6">
                  {searchTerm || selectedCompany 
                    ? 'Try adjusting your search criteria or filters.'
                    : 'You haven\'t created any research reports yet.'}
                </p>
                <button
                  onClick={handleCreateNew}
                  className="px-6 py-2 bg-gradient-to-r from-orange-500 to-amber-600 text-white font-semibold rounded-lg shadow-md hover:from-orange-600 hover:to-amber-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-opacity-50 transition-all duration-200"
                >
                  Create Your First Research
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-0"> {/* Removed space-y-3 and use individual margins instead */}
            {currentCDs.map(cd => (
              <div key={cd.id} style={cdCardStyle}> {/* Using cdCardStyle with increased margin */}
                <div 
                  style={containerStyle}
                  className="group cursor-pointer"
                  onClick={() => viewCDDetail(cd.id)}
                >
                  <div className="flex flex-col md:flex-row md:items-center">
                    <div className="px-4 py-3 md:w-3/4">
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between w-full">
                        <div>
                          <h3 className="text-lg font-bold text-white mb-1 font-playfair">
                            {capitalizeWords(cd.company_name)}
                          </h3>
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-1">
                            <p className="text-white/70 text-xs">
                              🌐 {cd.company_website}
                            </p>
                            {cd.stakeholders && (
                              <p className="text-white/70 text-xs">
                                👥 {cd.stakeholders}
                              </p>
                            )}
                            <span className="text-white/60 text-xs bg-white/10 px-2 py-0.5 rounded">
                              {formatDate(cd.timestamp)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="px-4 pb-3 md:py-3 md:w-1/4 md:flex md:justify-end md:pr-4">
                      <button
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent the card click from triggering
                          viewCDDetail(cd.id);
                        }}
                        className="w-full md:w-auto px-4 py-1.5 bg-gradient-to-r from-orange-500 to-amber-600 text-white font-medium text-sm rounded-md shadow-sm hover:from-orange-600 hover:to-amber-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-opacity-50 transition-all duration-200"
                      >
                        View Research
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination Component */}
        {filteredCDs.length > 0 && totalPages > 1 && (
          <div style={containerWrapperStyle} className="mt-8"> {/* Increased top margin */}
            <div style={containerStyle}>
              <div className="px-4 py-3 flex items-center justify-between">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={prevPage}
                    disabled={currentPage === 1}
                    className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                      currentPage === 1 
                        ? 'bg-white/5 text-gray-400 cursor-not-allowed' 
                        : 'bg-white/10 text-white hover:bg-white/20'
                    }`}
                  >
                    Previous
                  </button>
                  <button
                    onClick={nextPage}
                    disabled={currentPage === totalPages}
                    className={`ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                      currentPage === totalPages 
                        ? 'bg-white/5 text-gray-400 cursor-not-allowed' 
                        : 'bg-white/10 text-white hover:bg-white/20'
                    }`}
                  >
                    Next
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-white">
                      Showing <span className="font-medium">{indexOfFirstItem + 1}</span> to{' '}
                      <span className="font-medium">
                        {Math.min(indexOfLastItem, filteredCDs.length)}
                      </span>{' '}
                      of <span className="font-medium">{filteredCDs.length}</span> results
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                      <button
                        onClick={prevPage}
                        disabled={currentPage === 1}
                        className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 text-sm font-medium ${
                          currentPage === 1 
                            ? 'bg-white/5 text-gray-400 cursor-not-allowed' 
                            : 'bg-white/10 text-white hover:bg-white/20'
                        }`}
                      >
                        <span className="sr-only">Previous</span>
                        <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                          <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </button>
                      
                      {/* Page Numbers */}
                      {[...Array(totalPages)].map((_, index) => {
                        // Limit displayed page numbers to avoid clutter
                        const pageNum = index + 1;
                        const showPageNumber = 
                          pageNum === 1 || 
                          pageNum === totalPages || 
                          (pageNum >= currentPage - 1 && pageNum <= currentPage + 1);
                        
                        // Show ellipsis for skipped pages
                        if (!showPageNumber) {
                          if (pageNum === 2 || pageNum === totalPages - 1) {
                            return (
                              <span key={`ellipsis-${pageNum}`} className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white/5 text-sm font-medium text-gray-400">
                                ...
                              </span>
                            );
                          }
                          return null;
                        }
                        
                        return (
                          <button
                            key={pageNum}
                            onClick={() => paginate(pageNum)}
                            aria-current={currentPage === pageNum ? "page" : undefined}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                              currentPage === pageNum
                                ? 'z-10 bg-gradient-to-r from-orange-500 to-amber-600 border-orange-500 text-white'
                                : 'bg-white/10 border-gray-300 text-white hover:bg-white/20'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                      
                      <button
                        onClick={nextPage}
                        disabled={currentPage === totalPages}
                        className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 text-sm font-medium ${
                          currentPage === totalPages 
                            ? 'bg-white/5 text-gray-400 cursor-not-allowed' 
                            : 'bg-white/10 text-white hover:bg-white/20'
                        }`}
                      >
                        <span className="sr-only">Next</span>
                        <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                          <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Create New Button */}
        <div className="mt-8 text-center">
          <button
            onClick={handleCreateNew}
            className="px-6 py-2.5 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-lg border border-white/20 focus:outline-none focus:ring-2 focus:ring-white/50 focus:ring-opacity-50 transition-all duration-200 group"
          >
            <span className="inline-flex items-center">
              <svg className="w-4 h-4 mr-2 group-hover:text-orange-400 transition-colors duration-200" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Create New Research
            </span>
          </button>
        </div>

        {/* Error Message for Details */}
        {error && (
          <div className="mt-8 bg-red-500/20 border-l-4 border-red-500 p-6 rounded-lg">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-red-300">Error</h3>
                <p className="mt-1 text-white">{error}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ViewCDsPage;